import { z, SongSchema } from '@guestguru/shared';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

// ── API response shape (snake_case from @guestguru/shared) ──
// Extended with fields the backend returns that aren't in the core schema yet
type ApiSongRow = z.infer<typeof SongSchema> & {
  moment?: string;
  non_negotiable?: boolean;
  sort_order?: number;
};

export interface Song {
  id?: string;
  name: string;
  genre: string;
  artist: string;
  notes?: string;
  link?: string;
  type: string;
  moment?: string;
  non_negotiable?: boolean;
  sort_order?: number;
  archived?: boolean;
}

let songsCache: Song[] = [];

export function loadSongs(): Song[] {
  return songsCache;
}

export function saveSongs(songs: Song[]): void {
  songsCache = songs;
}

export async function refreshSongsFromApi(): Promise<Song[]> {
  try {
    const response = await apiGet<{ songs: ApiSongRow[] }>(planningPath('/songs'));
    if (Array.isArray(response?.songs) && response.songs.length > 0) {
      const normalised: Song[] = response.songs.map(row => ({
        id: String(row.id),
        name: row.name,
        artist: row.artist ?? '',
        genre: row.genre ?? '',
        type: row.song_type ?? 'After Party',
        notes: row.notes ?? undefined,
        link: row.link ?? undefined,
        moment: row.moment ?? undefined,
        non_negotiable: row.non_negotiable ?? false,
        sort_order: row.sort_order ?? undefined,
      }));
      saveSongs(normalised);
      return normalised;
    }
  } catch {
    // API unreachable — keep existing in-memory cache
  }
  return loadSongs();
}

export async function addSong(song: Song): Promise<Song[]> {
  // The server owns the id and /songs/:id parses it with Number(), so a song
  // cached without one can never be updated, archived or deleted afterwards.
  // Create server-side first and adopt the real id instead of caching an
  // optimistic row the API has no address for.
  const created = await apiPost<{ id: number | string }>(planningPath('/songs'), {
    name: song.name,
    artist: song.artist,
    genre: song.genre,
    song_type: song.type,
    notes: song.notes ?? null,
    link: song.link ?? null,
    moment: song.moment ?? null,
    non_negotiable: song.non_negotiable ?? false,
    sort_order: song.sort_order ?? null,
  });

  const updated = [...loadSongs(), { ...song, id: String(created.id) }];
  saveSongs(updated);

  return updated;
}

export async function updateSong(updated: Song): Promise<Song[]> {
  if (!updated.id) throw new Error('This song was never saved to the server — reload the page and add it again.');

  // Persist before touching the cache. Writing the cache first made a rejected
  // PUT indistinguishable from a successful one: the edit stayed on screen
  // until the next refresh silently replaced it with the server's stale row.
  await apiPut(planningPath(`/songs/${updated.id}`), {
    name: updated.name,
    artist: updated.artist,
    genre: updated.genre,
    song_type: updated.type,
    notes: updated.notes ?? null,
    link: updated.link ?? null,
    moment: updated.moment ?? null,
    non_negotiable: updated.non_negotiable ?? false,
    sort_order: updated.sort_order ?? null,
  });

  const next = loadSongs().map(s => (s.id === updated.id ? { ...s, ...updated } : s));
  saveSongs(next);

  return next;
}

/** Soft-delete: mark as archived (hidden from the main list, recoverable) */
export async function archiveSong(id: string): Promise<Song[]> {
  await apiPut(planningPath(`/songs/${id}`), { archived: true });
  const next = loadSongs().map(s => (s.id === id ? { ...s, archived: true } : s));
  saveSongs(next);
  return next;
}

/** Restore an archived song back to active */
export async function restoreSong(id: string): Promise<Song[]> {
  await apiPut(planningPath(`/songs/${id}`), { archived: false });
  const next = loadSongs().map(s => (s.id === id ? { ...s, archived: false } : s));
  saveSongs(next);
  return next;
}

/**
 * Permanent delete — only allowed for already-archived songs.
 *
 * Throws rather than returning the unchanged list: a refusal used to be
 * reported to the caller as the same `Song[]` a successful delete returns,
 * so a song that could not be deleted looked exactly like one that was.
 */
export async function deleteSong(name: string, type: string): Promise<Song[]> {
  const current = loadSongs();
  const target = current.find(s => s.name === name && s.type === type);
  if (!target) throw new Error('That song is no longer in the playlist — refresh and try again.');
  if (!target.archived) throw new Error('Archive this song before deleting it permanently.');
  if (!target.id) throw new Error('This song was never saved to the server — reload the page and try again.');

  // Delete server-side first; dropping the row from the cache before the
  // DELETE landed is what made a failed delete look like a success.
  await apiDelete(planningPath(`/songs/${target.id}`));

  const updated = current.filter(s => !(s.name === name && s.type === type));
  saveSongs(updated);

  return updated;
}
